import { getFileTimestamp } from '@/lib/utils';
import { ShiftSet, ShiftType, StaffGroup, Tag } from '@/types/template';
import type { StaffFilters } from '@repo/contracts';

const XLSX = await import('xlsx');

import {
  DAY_PATTERN_DAYS,
  daysToSchedule,
  detectDayPattern,
  englishDayLabel,
  scheduleToDays,
} from './day-of-week';

export interface ParsedShiftData {
  row: number;
  shiftType: string;
  daysOfWeek: string;
  startTime: string;
  endTime: string;
  employeesNeeded: string;
  staffGroups?: string;
  tags?: string;
  staffingMethod?: string;
  assignedEmployeeId?: string;
}

export interface ValidationError {
  row: number;
  column?: string;
  message: string;
}

// Input-side aliases for the spreadsheet's day-pattern column. The
// canonical preset → days mapping lives in DAY_PATTERN_DAYS; the
// extra `'every day'` entry covers the user-input variant with a
// space that the wizard's controlled `DayPattern` type doesn't allow.
const DAY_PATTERNS: Record<string, number[]> = {
  ...DAY_PATTERN_DAYS,
  'every day': DAY_PATTERN_DAYS.everyday,
};

// ISO 8601 day numbering (1 = Mon … 7 = Sun), matching the contract ShiftSchedule.
const DAY_NAMES: Record<string, number> = {
  monday: 1,
  mon: 1,
  tuesday: 2,
  tue: 2,
  wednesday: 3,
  wed: 3,
  thursday: 4,
  thu: 4,
  friday: 5,
  fri: 5,
  saturday: 6,
  sat: 6,
  sunday: 7,
  sun: 7,
};

export async function parseExcelFile(file: File): Promise<ParsedShiftData[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });

        // Get the first sheet (or "Shift Data" sheet if it exists)
        let sheetName = workbook.SheetNames[0];
        if (workbook.SheetNames.includes('Shift Data')) {
          sheetName = 'Shift Data';
        }
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as string[][];

        if (jsonData.length < 2) {
          reject(new Error('File contains no shift data'));
          return;
        }

        // Validate headers (case-insensitive)
        const headers = jsonData[0].map((h) =>
          String(h || '')
            .toLowerCase()
            .trim(),
        );
        const requiredHeaders = ['shift type', 'days of week', 'start time', 'end time'];

        // Check for required headers
        const hasRequiredHeaders = requiredHeaders.every((required) =>
          headers.some((header) => header === required || header.includes(required.split(' ')[0])),
        );

        if (!hasRequiredHeaders) {
          reject(
            new Error(
              'File must have at least 4 required columns: Shift Type, Days of Week, Start Time, End Time',
            ),
          );
          return;
        }

        // Detect column indices
        const colIndices = {
          shiftType: headers.findIndex((h) => h === 'shift type' || h.includes('shift')),
          daysOfWeek: headers.findIndex((h) => h === 'days of week' || h.includes('days')),
          startTime: headers.findIndex((h) => h === 'start time' || h.includes('start')),
          endTime: headers.findIndex((h) => h === 'end time' || h.includes('end')),
          employeesNeeded: headers.findIndex((h) => h.includes('employee') || h.includes('needed')),
          staffGroups: headers.findIndex((h) => h.includes('staff') && h.includes('group')),
          tags: headers.findIndex((h) => h === 'tags' || h === 'tag'),
          staffingMethod: headers.findIndex((h) => h.includes('staffing') && h.includes('method')),
          assignedEmployeeId: headers.findIndex(
            (h) => h.includes('assigned') && h.includes('employee'),
          ),
        };

        // Parse data rows
        const parsedData: ParsedShiftData[] = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];

          // Skip empty rows
          if (!row || row.every((cell) => !cell)) {
            continue;
          }

          const parsed: ParsedShiftData = {
            row: i + 1,
            shiftType: String(row[colIndices.shiftType] || '').trim(),
            daysOfWeek: String(row[colIndices.daysOfWeek] || '').trim(),
            startTime: String(row[colIndices.startTime] || '').trim(),
            endTime: String(row[colIndices.endTime] || '').trim(),
            employeesNeeded:
              colIndices.employeesNeeded >= 0
                ? String(row[colIndices.employeesNeeded] || '').trim()
                : '1',
          };

          // Add optional columns if they exist
          if (colIndices.staffGroups >= 0) {
            parsed.staffGroups = String(row[colIndices.staffGroups] || '').trim();
          }
          if (colIndices.tags >= 0) {
            parsed.tags = String(row[colIndices.tags] || '').trim();
          }
          if (colIndices.staffingMethod >= 0) {
            parsed.staffingMethod = String(row[colIndices.staffingMethod] || '').trim();
          }
          if (colIndices.assignedEmployeeId >= 0) {
            parsed.assignedEmployeeId = String(row[colIndices.assignedEmployeeId] || '').trim();
          }

          parsedData.push(parsed);
        }

        resolve(parsedData);
      } catch {
        reject(new Error('Unable to read file. Please ensure it is a valid Excel file'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Unable to read file. Please try again'));
    };

    reader.readAsBinaryString(file);
  });
}

function parseDaysOfWeek(daysString: string): number[] | null {
  const normalized = daysString.toLowerCase().trim();

  // Check for patterns
  if (DAY_PATTERNS[normalized]) {
    return DAY_PATTERNS[normalized];
  }

  // Check for comma-separated days
  if (daysString.includes(',')) {
    const days = daysString.split(',').map((d) => d.trim().toLowerCase());
    const dayNumbers: number[] = [];

    for (const day of days) {
      if (DAY_NAMES[day] !== undefined) {
        dayNumbers.push(DAY_NAMES[day]);
      } else {
        return null; // Invalid day name
      }
    }

    return [...new Set(dayNumbers)].sort((a, b) => a - b);
  }

  // Check for single day
  if (DAY_NAMES[normalized] !== undefined) {
    return [DAY_NAMES[normalized]];
  }

  return null;
}

function parseTime(timeString: string): string | null {
  // Remove any whitespace
  const cleaned = timeString.trim();

  // Check for HH:MM format
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
  const match = cleaned.match(timeRegex);

  if (match) {
    const hours = match[1].padStart(2, '0');
    const minutes = match[2];
    return `${hours}:${minutes}`;
  }

  return null;
}

function parseStaffGroups(
  staffGroupsString: string,
  availableGroups: StaffGroup[],
): { ids: string[]; errors: string[] } {
  if (!staffGroupsString || staffGroupsString.trim() === '') {
    return { ids: [], errors: [] };
  }

  const groupNames = staffGroupsString
    .split(',')
    .map((g) => g.trim())
    .filter((g) => g);
  const ids: string[] = [];
  const errors: string[] = [];

  for (const groupName of groupNames) {
    const group = availableGroups.find((g) => g.name.toLowerCase() === groupName.toLowerCase());
    if (group) {
      ids.push(group.id);
    } else {
      errors.push(groupName);
    }
  }

  return { ids, errors };
}

function parseTags(tagsString: string, availableTags: Tag[]): { ids: string[]; errors: string[] } {
  if (!tagsString || tagsString.trim() === '') {
    return { ids: [], errors: [] };
  }

  const tagNames = tagsString
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t);
  const ids: string[] = [];
  const errors: string[] = [];

  for (const tagName of tagNames) {
    const tag = availableTags.find((t) => t.name.toLowerCase() === tagName.toLowerCase());
    if (tag) {
      ids.push(tag.id);
    } else {
      errors.push(tagName);
    }
  }

  return { ids, errors };
}

export function validateShiftData(
  data: ParsedShiftData[],
  shiftTypes: ShiftType[],
  staffGroups: StaffGroup[],
  tags: Tag[],
): { validShifts: ShiftSet[]; errors: ValidationError[] } {
  const validShifts: ShiftSet[] = [];
  const errors: ValidationError[] = [];

  const shiftTypeNames = shiftTypes.map((st) => st.name.toLowerCase());

  for (const item of data) {
    const rowErrors: ValidationError[] = [];

    // Validate shift type
    if (!item.shiftType) {
      rowErrors.push({ row: item.row, column: 'A', message: 'Shift Type is required' });
    } else {
      const normalizedType = item.shiftType.toLowerCase();
      if (!shiftTypeNames.includes(normalizedType)) {
        const validTypes = shiftTypes.map((st) => st.name).join(', ');
        rowErrors.push({
          row: item.row,
          column: 'A',
          message: `'${item.shiftType}' is not a valid shift type. Valid types: ${validTypes}`,
        });
      }
    }

    // Validate days of week
    if (!item.daysOfWeek) {
      rowErrors.push({ row: item.row, column: 'B', message: 'Days of Week is required' });
    } else {
      const days = parseDaysOfWeek(item.daysOfWeek);
      if (!days) {
        rowErrors.push({
          row: item.row,
          column: 'B',
          message: `'${item.daysOfWeek}' is not valid. Use 'Weekdays', 'Weekends', 'Every day', or specific days (e.g., 'Mon,Wed,Fri')`,
        });
      }
    }

    // Validate start time
    if (!item.startTime) {
      rowErrors.push({ row: item.row, column: 'C', message: 'Start Time is required' });
    } else {
      const startTime = parseTime(item.startTime);
      if (!startTime) {
        rowErrors.push({
          row: item.row,
          column: 'C',
          message: 'Start Time must be in HH:MM format (e.g., 09:00)',
        });
      }
    }

    // Validate end time
    if (!item.endTime) {
      rowErrors.push({ row: item.row, column: 'D', message: 'End Time is required' });
    } else {
      const endTime = parseTime(item.endTime);
      if (!endTime) {
        rowErrors.push({
          row: item.row,
          column: 'D',
          message: 'End Time must be in HH:MM format (e.g., 17:00)',
        });
      }
    }

    // Validate employees needed (optional, defaults to 1)
    let employeeCount = 1;
    if (item.employeesNeeded && item.employeesNeeded.trim() !== '') {
      const parsed = parseInt(item.employeesNeeded, 10);
      if (isNaN(parsed) || parsed < 1) {
        rowErrors.push({
          row: item.row,
          column: 'E',
          message: 'Employees needed must be a positive number (1-99)',
        });
      } else if (parsed > 99) {
        rowErrors.push({
          row: item.row,
          column: 'E',
          message: 'Employees needed cannot exceed 99',
        });
      } else {
        employeeCount = parsed;
      }
    }

    // Validate staff groups (optional)
    let staffGroupIds: string[] = [];
    if (item.staffGroups && item.staffGroups.trim() !== '') {
      const { ids, errors: groupErrors } = parseStaffGroups(item.staffGroups, staffGroups);
      staffGroupIds = ids;
      if (groupErrors.length > 0) {
        const validGroups = staffGroups.map((g) => g.name).join(', ');
        rowErrors.push({
          row: item.row,
          column: 'F',
          message: `Staff group(s) not found: '${groupErrors.join("', '")}'. Valid groups: ${validGroups}`,
        });
      }
    }

    // Validate tags (optional)
    let tagIds: string[] = [];
    if (item.tags && item.tags.trim() !== '') {
      const { ids, errors: tagErrors } = parseTags(item.tags, tags);
      tagIds = ids;
      if (tagErrors.length > 0) {
        const validTags = tags.map((t) => t.name).join(', ');
        rowErrors.push({
          row: item.row,
          column: 'G',
          message: `Tag(s) not found: '${tagErrors.join("', '")}'. Valid tags: ${validTags}`,
        });
      }
    }

    // If there are errors, add them and continue
    if (rowErrors.length > 0) {
      errors.push(...rowErrors);
      continue;
    }

    // Create valid shift pattern
    const shiftType = shiftTypes.find(
      (st) => st.name.toLowerCase() === item.shiftType.toLowerCase(),
    );
    const days = parseDaysOfWeek(item.daysOfWeek);
    const startTime = parseTime(item.startTime);
    const endTime = parseTime(item.endTime);
    // The row passed validation above, so these always parse; narrow for the
    // type system rather than asserting non-null.
    if (days === null || startTime === null || endTime === null) continue;

    const resolvedGroups = staffGroupIds
      .map((id) => staffGroups.find((g) => g.id === id))
      .filter((g): g is StaffGroup => g !== undefined);
    const resolvedTags = tagIds
      .map((id) => tags.find((t) => t.id === id))
      .filter((t): t is Tag => t !== undefined);

    const filters: StaffFilters = {};
    if (resolvedGroups.length > 0) filters.staffGroups = resolvedGroups;
    if (resolvedTags.length > 0) filters.tags = resolvedTags;

    const shiftSet: ShiftSet = {
      id: crypto.randomUUID(),
      name: shiftType?.name || item.shiftType,
      schedule: daysToSchedule(days),
      shiftType: { id: shiftType?.id ?? '', name: shiftType?.name ?? item.shiftType },
      startTime,
      endTime,
      staffing: {
        method: 'automatic',
        count: employeeCount,
        filters,
      },
    };

    validShifts.push(shiftSet);
  }

  return { validShifts, errors };
}

export function generateTemplateFile(
  shiftTypes: ShiftType[],
  staffGroups: StaffGroup[],
  tags: Tag[],
) {
  // Create workbook
  const wb = XLSX.utils.book_new();

  // Sheet 1: Instructions
  const instructionsData = [
    ['Shift Pattern Import Template - Instructions'],
    [''],
    ['Purpose:'],
    ['This template allows you to bulk import shift patterns into your rota template.'],
    ['You can import complete shift pattern data including staffing requirements.'],
    [''],
    ['How to use:'],
    ['1. Fill in the "Shift Data" sheet with your shift information'],
    ['2. Required columns (A-D) must be completed for all rows'],
    ['3. Optional columns (E-G) can be left empty to use defaults'],
    ['4. Save the file and upload it through the import dialog'],
    ['5. Review any validation errors and fix them if needed'],
    ['6. Import the shifts - staffing details will be populated automatically'],
    [''],
    ['Column Descriptions:'],
    [''],
    ['REQUIRED COLUMNS:'],
    [''],
    ['Column A - Shift Type (Required):'],
    ['- Must match an existing shift type in your system exactly'],
    ['- Case-insensitive matching'],
    ['- See "Reference Data" sheet for valid shift types'],
    [''],
    ['Column B - Days of Week (Required):'],
    ['- Use one of these patterns:'],
    ['  • "Weekdays" (Mon-Fri)'],
    ['  • "Weekends" (Sat-Sun)'],
    ['  • "Every day" (Mon-Sun)'],
    ['  • Specific days: "Mon,Wed,Fri" or "Monday,Wednesday,Friday"'],
    ['- Accepts abbreviated or full day names'],
    ['- Case-insensitive'],
    [''],
    ['Column C - Start Time (Required):'],
    ['- Format: HH:MM (24-hour format)'],
    ['- Examples: 09:00, 17:30, 23:45'],
    [''],
    ['Column D - End Time (Required):'],
    ['- Format: HH:MM (24-hour format)'],
    ['- Can be after midnight for overnight shifts (e.g., 22:00 to 02:00)'],
    [''],
    ['OPTIONAL COLUMNS:'],
    [''],
    ['Column E - Employees Needed (Optional):'],
    ['- Whole number from 1-99'],
    ['- Default: 1 (if empty)'],
    ['- Examples: 2, 3, 5'],
    ['- This sets how many employees are needed for each shift'],
    [''],
    ['Column F - Staff Groups (Optional):'],
    ['- Comma-separated list of staff group names'],
    ['- Default: No filtering (all staff groups eligible)'],
    ['- Case-insensitive matching'],
    ['- Examples: "Kitchen", "Kitchen,Bar", "Front of House"'],
    ['- See "Reference Data" sheet for valid staff groups'],
    [''],
    ['Column G - Tags (Optional):'],
    ['- Comma-separated list of tag names'],
    ['- Default: No tag requirements'],
    ['- Case-insensitive matching'],
    ['- Examples: "Chef", "Chef,Line Cook", "Bartender,Keyholder"'],
    ['- See "Reference Data" sheet for valid tags'],
    [''],
    ['Column H - Staffing Method (Optional):'],
    ['- How the shift should be staffed'],
    ['- Valid values: "Auto", "Open", "Fixed"'],
    ['- Default: "Auto" (automatically assign employees)'],
    ['- "Auto" - System automatically assigns eligible employees'],
    ['- "Open" - Creates an open shift that employees can claim'],
    ['- "Fixed" - Assigns shift to a specific employee (requires Column I)'],
    ['- Case-insensitive'],
    [''],
    ['Column I - Assigned Employee ID (Optional):'],
    ['- Employee user ID for fixed assignments'],
    ['- Only used when Column H is "Fixed"'],
    ['- Must be a valid employee ID from your system'],
    ['- Leave empty for "Auto" or "Open" staffing methods'],
    ['- Example: "EMP001", "12345"'],
    [''],
    ['Tips:'],
    ['- Leave optional columns empty to use defaults'],
    ['- Maximum 100 shifts per import'],
    ['- File size must be under 5MB'],
    ['- Check the "Reference Data" sheet for valid values'],
    ['- Use "Open" staffing method for shifts employees can claim themselves'],
    ['- Use "Fixed" staffing method to assign shifts to specific employees'],
  ];

  const wsInstructions = XLSX.utils.aoa_to_sheet(instructionsData);
  wsInstructions['!cols'] = [{ wch: 80 }];
  XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');

  // Sheet 2: Shift Data
  const shiftData = [
    [
      'Shift Type',
      'Days of Week',
      'Start Time',
      'End Time',
      'Employees Needed',
      'Staff Groups',
      'Tags',
      'Staffing Method',
      'Assigned Employee ID',
    ],
    [
      'Chef',
      'Weekdays',
      '09:00',
      '17:00',
      '2',
      'Kitchen',
      'Chef,Food Safety Certified',
      'Auto',
      '',
    ],
    ['Bartender', 'Weekends', '18:00', '02:00', '3', 'Bar', 'Bartender,Mixologist', '', ''],
    ['Server', 'Every day', '11:00', '15:00', '', 'Front of House', 'Server', 'Open', ''],
    ['Kitchen Porter', 'Mon,Wed,Fri', '14:00', '20:00', '1', '', '', 'Fixed', 'EMP001'],
    ['Line Cook', 'Weekdays', '10:00', '18:00', '', 'Kitchen', '', '', ''],
    ['', '', '', '', '', '', '', '', ''],
  ];

  const wsShiftData = XLSX.utils.aoa_to_sheet(shiftData);
  wsShiftData['!cols'] = [
    { wch: 20 }, // Shift Type
    { wch: 20 }, // Days of Week
    { wch: 12 }, // Start Time
    { wch: 12 }, // End Time
    { wch: 18 }, // Employees Needed
    { wch: 25 }, // Staff Groups
    { wch: 30 }, // Tags
    { wch: 18 }, // Staffing Method
    { wch: 22 }, // Assigned Employee ID
  ];

  // Freeze header row
  wsShiftData['!freeze'] = { xSplit: 0, ySplit: 1 };

  XLSX.utils.book_append_sheet(wb, wsShiftData, 'Shift Data');

  // Sheet 3: Reference Data
  const referenceData: string[][] = [['VALID SHIFT TYPES:']];
  shiftTypes.forEach((st) => {
    referenceData.push([st.concatenatedName || st.name]);
  });

  referenceData.push(['']);
  referenceData.push(['VALID STAFF GROUPS:']);

  // Add staff groups from system
  staffGroups.forEach((group) => {
    referenceData.push([group.name]);
  });

  referenceData.push(['']);
  referenceData.push(['VALID TAGS:']);

  // Add tags from system
  tags.forEach((tag) => {
    referenceData.push([tag.name]);
  });

  referenceData.push(['']);
  referenceData.push(['VALID DAY PATTERNS:']);
  referenceData.push(['Weekdays (Mon-Fri)']);
  referenceData.push(['Weekends (Sat-Sun)']);
  referenceData.push(['Every day (Mon-Sun)']);
  referenceData.push(['Specific days: Mon,Wed,Fri']);
  referenceData.push(['']);
  referenceData.push(['VALID STAFFING METHODS:']);
  referenceData.push(['Auto (Automatically assign employees)']);
  referenceData.push(['Open (Employees can claim the shift)']);
  referenceData.push(['Fixed (Assign to specific employee)']);
  referenceData.push(['']);
  referenceData.push(['TIME FORMAT EXAMPLES:']);
  referenceData.push(['09:00 (9:00 AM)']);
  referenceData.push(['17:30 (5:30 PM)']);
  referenceData.push(['23:45 (11:45 PM)']);
  referenceData.push(['02:00 (2:00 AM - for overnight shifts)']);

  const wsReference = XLSX.utils.aoa_to_sheet(referenceData);
  wsReference['!cols'] = [{ wch: 40 }];
  XLSX.utils.book_append_sheet(wb, wsReference, 'Reference Data');

  // Generate and download
  XLSX.writeFile(wb, 'shift_pattern_import_template.xlsx');
}

export function generateErrorReport(data: ParsedShiftData[], errors: ValidationError[]) {
  const wb = XLSX.utils.book_new();

  // Create error map
  const errorMap = new Map<number, string[]>();
  errors.forEach((error) => {
    const existing = errorMap.get(error.row) || [];
    const errorMsg = error.column ? `Column ${error.column}: ${error.message}` : error.message;
    existing.push(errorMsg);
    errorMap.set(error.row, existing);
  });

  // Create data with errors column
  const reportData = [
    [
      'Row',
      'Shift Type',
      'Days of Week',
      'Start Time',
      'End Time',
      'Employees Needed',
      'Staff Groups',
      'Tags',
      'Errors',
    ],
  ];

  data.forEach((item) => {
    const rowErrors = errorMap.get(item.row) || [];
    reportData.push([
      item.row.toString(),
      item.shiftType,
      item.daysOfWeek,
      item.startTime,
      item.endTime,
      item.employeesNeeded,
      item.staffGroups || '',
      item.tags || '',
      rowErrors.join('; '),
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(reportData);
  ws['!cols'] = [
    { wch: 6 }, // Row
    { wch: 18 }, // Shift Type
    { wch: 18 }, // Days of Week
    { wch: 12 }, // Start Time
    { wch: 12 }, // End Time
    { wch: 18 }, // Employees Needed
    { wch: 25 }, // Staff Groups
    { wch: 30 }, // Tags
    { wch: 60 }, // Errors
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Error Report');

  // Add instructions sheet
  const instructionsData = [
    ['Error Report - How to Fix'],
    [''],
    [
      'This file contains your original data with an "Errors" column showing what needs to be fixed.',
    ],
    [''],
    ['Steps to fix:'],
    ['1. Review the "Errors" column for each row'],
    ['2. Correct the issues in the corresponding columns'],
    ['3. Delete the "Errors" column (Column I)'],
    ['4. Save the file'],
    ['5. Upload the fixed file through the import dialog'],
    [''],
    ['Common Errors:'],
    [''],
    ['Column A - Invalid Shift Type:'],
    ['- Make sure the shift type matches exactly (case-insensitive)'],
    ['- Check the valid shift types in the Reference Data sheet'],
    [''],
    ['Column B - Invalid Days:'],
    ['- Use "Weekdays", "Weekends", "Every day", or specific days like "Mon,Wed,Fri"'],
    ['- Make sure day names are spelled correctly'],
    [''],
    ['Column C/D - Invalid Time Format:'],
    ['- Use HH:MM format (e.g., 09:00, 17:30)'],
    ['- Use 24-hour format'],
    [''],
    ['Column E - Invalid Employees Needed:'],
    ['- Must be a positive number from 1-99'],
    ['- Leave empty to default to 1'],
    [''],
    ['Column F - Invalid Staff Groups:'],
    ['- Check spelling and use exact names from Reference Data sheet'],
    ['- Separate multiple groups with commas: "Kitchen,Bar"'],
    ['- Leave empty if no filtering needed'],
    [''],
    ['Column G - Invalid Tags:'],
    ['- Check spelling and use exact names from Reference Data sheet'],
    ['- Separate multiple tags with commas: "Chef,Line Cook"'],
    ['- Leave empty if no tag requirements'],
  ];

  const wsInstructions = XLSX.utils.aoa_to_sheet(instructionsData);
  wsInstructions['!cols'] = [{ wch: 80 }];
  XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');

  // Generate and download
  const timestamp = getFileTimestamp();
  XLSX.writeFile(wb, `shift_import_errors_${timestamp}.xlsx`);
}

/**
 * Exports a template's shifts to an Excel workbook.
 *
 * Shift-type names are resolved from the `shiftTypes` reference data by id
 * (falling back to any name stored on the shift, then "Unknown"). Stored
 * templates persist the shift-type id with an empty display name — the UI
 * resolves the name from reference data at render time — so without this
 * lookup the export column reads "Unknown".
 */
export function exportTemplateToExcel(
  templateName: string,
  shifts: ShiftSet[],
  shiftTypes: ShiftType[] = [],
) {
  const wb = XLSX.utils.book_new();

  // Convert shift patterns to export format
  const exportData: (string | number)[][] = [
    [
      'Shift Type',
      'Days of Week',
      'Start Time',
      'End Time',
      'Employees Needed',
      'Staff Groups',
      'Tags',
    ],
  ];

  shifts.forEach((shiftSet) => {
    const resolvedShiftType = shiftTypes.find((st) => st.id === shiftSet.shiftType.id);
    const shiftTypeName = resolvedShiftType?.name || shiftSet.shiftType.name || 'Unknown';

    // Convert days array to readable format
    const daysOfWeek = formatDaysForExport(scheduleToDays(shiftSet.schedule));

    // Format times
    const startTime = shiftSet.startTime;
    const endTime = shiftSet.endTime;

    // Staffing details: specific assignments are a single named person and
    // carry no filters; open/automatic carry a count and optional filters.
    const staffing = shiftSet.staffing;
    const employeesNeeded = staffing.method === 'specific' ? 1 : staffing.count;
    const filters = staffing.method === 'specific' ? undefined : staffing.filters;

    // Get staff group names
    const staffGroupNames = (filters?.staffGroups ?? [])
      .map((group) => group.name)
      .filter(Boolean)
      .join(',');

    // Get tag names
    const tagNames = (filters?.tags ?? [])
      .map((tag) => tag.name)
      .filter(Boolean)
      .join(',');

    exportData.push([
      shiftTypeName,
      daysOfWeek,
      startTime,
      endTime,
      employeesNeeded,
      staffGroupNames,
      tagNames,
    ]);
  });

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(exportData);

  // Set column widths
  ws['!cols'] = [
    { wch: 20 }, // Shift Type
    { wch: 20 }, // Days of Week
    { wch: 12 }, // Start Time
    { wch: 12 }, // End Time
    { wch: 18 }, // Employees Needed
    { wch: 25 }, // Staff Groups
    { wch: 30 }, // Tags
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Shift Data');

  // Download file
  const sanitizedName = templateName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  const timestamp = getFileTimestamp();
  XLSX.writeFile(wb, `${sanitizedName}_${timestamp}.xlsx`);
}

// Helper function to format days for export
function formatDaysForExport(days: number[]): string {
  const sortedDays = [...days].sort((a, b) => a - b);
  const pattern = detectDayPattern(sortedDays);
  if (pattern === 'everyday') return 'Every day';
  if (pattern === 'weekdays') return 'Weekdays';
  if (pattern === 'weekends') return 'Weekends';
  return sortedDays.map((d) => englishDayLabel(d)).join(',');
}
